import { apiClient } from "./api";

export interface AtelierAgentTemplate {
  id: string;
  name: string;
  description: string;
  profile: Record<string, unknown>;
}

export interface AtelierAgent {
  id: string;
  name?: string;
  profile_id?: string;
  reused?: boolean;
  [key: string]: unknown;
}

export interface AtelierSkill {
  id: string;
  name?: string;
  display_name?: string;
  description?: string;
  [key: string]: unknown;
}

export interface AttachSkillInput {
  name: string;
  display_name?: string;
  description?: string;
  definition?: Record<string, unknown>;
  priority?: number;
  is_enabled?: boolean;
}

export interface KnowledgeProbeResult {
  reachable: boolean;
  degraded?: boolean;
  error?: string;
}

export interface ProvisionItem {
  name: string;
  id?: string;
  profile_id?: string;
  error?: string;
}

export interface ProvisionResult {
  created: ProvisionItem[];
  reused: ProvisionItem[];
  failed: ProvisionItem[];
  total: number;
  force: boolean;
}

export interface ProvisionStatus {
  provisioned: boolean;
  detail?: string;
  created?: ProvisionItem[];
  reused?: ProvisionItem[];
  failed?: ProvisionItem[];
  total?: number;
}

export const AGENT_TEMPLATES: AtelierAgentTemplate[] = [
  {
    id: "nocode-designer",
    name: "零代码设计助手",
    description: "根据自然语言需求生成完整的零代码应用 Schema、页面和数据模型。",
    profile: {
      name: "零代码设计助手",
      role: "nocode-designer",
      system_prompt:
        "你是罢歌亭的零代码设计助手。根据用户需求生成 JSON Schema，包含 pages、dataModels、permissions、theme。",
    },
  },
  {
    id: "data-modeler",
    name: "数据模型设计助手",
    description: "专注于设计 PostgreSQL 数据表、字段类型、关联关系与 RLS 策略。",
    profile: {
      name: "数据模型设计助手",
      role: "data-modeler",
      system_prompt:
        "你是罢歌亭的数据模型设计助手。根据业务需求设计数据表、字段、索引和 RLS 策略。",
    },
  },
  {
    id: "workflow-designer",
    name: "工作流设计助手",
    description: "将业务规则转化为触发器、条件分支与动作节点组成的工作流。",
    profile: {
      name: "工作流设计助手",
      role: "workflow-designer",
      system_prompt:
        "你是罢歌亭的工作流设计助手。根据业务规则生成 trigger、condition、action 节点的工作流 JSON。",
    },
  },
  {
    id: "code-doctor",
    name: "代码医生",
    description: "审查生成的 Schema 与代码，发现潜在问题并给出修复建议。",
    profile: {
      name: "代码医生",
      role: "code-doctor",
      system_prompt:
        "你是罢歌亭的代码医生。审查 JSON Schema 与 React 代码，找出错误、性能问题和安全隐患。",
    },
  },
  {
    id: "dev-assistant",
    name: "通用开发助手",
    description: "回答技术问题、解释代码、协助调试与二次开发。",
    profile: {
      name: "通用开发助手",
      role: "dev-assistant",
      system_prompt:
        "你是罢歌亭的通用开发助手，帮助技术用户理解、调试和扩展零代码应用。",
    },
  },
];

export async function createAtelierAgent(templateId: string, name?: string): Promise<AtelierAgent> {
  const template = AGENT_TEMPLATES.find((t) => t.id === templateId);
  const profile = template?.profile ?? {};
  const finalName = name?.trim() || (profile.name as string | undefined) || templateId;
  // 与后端 atelier_client 对齐：name + persona_config（人设原样透传 Atelier）
  const response = await apiClient.post("/atelier/agents", {
    name: finalName,
    description: template?.description ?? "",
    domain: "software_development",
    persona_config: {
      role: (profile.role as string | undefined) ?? "",
      system_prompt: (profile.system_prompt as string | undefined) ?? "",
      description: template?.description ?? "",
    },
  });
  return response.data;
}

/** 技能按 profile 维度挂在 agent 下，需带 agent_id 查询。 */
export async function listAtelierSkills(agentId: string): Promise<AtelierSkill[]> {
  const response = await apiClient.get("/atelier/skills", { params: { agent_id: agentId } });
  return response.data;
}

/** 幂等挂载技能（AgentSkillCreate 全量形状，同名复用）。 */
export async function attachSkill(agentId: string, skill: AttachSkillInput): Promise<AtelierSkill> {
  const response = await apiClient.post(`/atelier/agents/${agentId}/skills`, skill);
  return response.data;
}

/** best-effort 探活知识查询链路（Atelier 无 attach-by-id 端点）。 */
export async function attachKnowledge(agentId: string, query?: string): Promise<KnowledgeProbeResult> {
  const response = await apiClient.post(`/atelier/agents/${agentId}/knowledge`, { query });
  return response.data;
}

export async function deployAtelierRuntime(agentId: string): Promise<AtelierAgent> {
  const response = await apiClient.post(`/atelier/agents/${agentId}/runtime`);
  return response.data;
}

/** 触发在 Atelier 构建 extensions/agents 的代码生成 Agent（幂等）。 */
export async function provisionAgents(force = false): Promise<ProvisionResult> {
  const response = await apiClient.post("/atelier/provision", { force });
  return response.data;
}

export async function getProvisionStatus(): Promise<ProvisionStatus> {
  const response = await apiClient.get("/atelier/provision/status");
  return response.data;
}
